import { CallStatusesMap, DataRoomCall } from '../../shared/models';
import { ButtonGroup, Button, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface ICallsActionButtonProps {
	call: DataRoomCall;
	onEdit: (call: DataRoomCall) => void;
	onReset: (call: DataRoomCall) => void;
	onConfirm: (call: DataRoomCall) => void;
}

type CallsActionItem = keyof typeof CallsActionItemMap;
const CallsActionItemMap = {
	Edit: 'Edit',
	Reset: 'Reset Call',
	Complete: 'Mark Call Complete',
};

export const CallsActionButton: React.FC<ICallsActionButtonProps> = ({ call, onEdit, onReset, onConfirm }) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const canEdit = call.callStatus != 'Completed';
	const canConfirm = call.callStatus == CallStatusesMap.Scheduled;
	const canReset = call.callStatus == CallStatusesMap.Completed;
	const showActions = canEdit || canConfirm || canReset;

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: CallsActionItem) => {
		switch (action) {
			case 'Edit':
				onEdit(call);
				break;

			case 'Reset':
				onReset(call);
				break;

			case 'Complete':
				onConfirm(call);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: CallsActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return showActions ? (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									{canEdit && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
											{CallsActionItemMap['Edit']}
										</MenuItem>
									)}
									{ call.callStatus == CallStatusesMap.Completed && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Reset')}>
											{CallsActionItemMap['Reset']}
										</MenuItem>
									)}
									{call.callStatus == CallStatusesMap.Scheduled && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Complete')}>
											{CallsActionItemMap['Complete']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	) : (
		<></>
	);
};
